/**
 * Assistant query options (#765, spec §8.1).
 *
 * Keys carry `selectedOrgId` so an organization switch never serves the
 * previous organization's threads or quota from cache; `retry` is the shared
 * transient-only predicate — a 403 `tier_not_eligible` or a 429 does not
 * change its mind when asked twice.
 */

import { queryOptions } from '@tanstack/react-query';
import type { ApiClient } from '@/lib/api';
import { retryTransientOnly } from '@/lib/query-retry';
import {
  ASSISTANT_PROXY_DEADLINE_MS,
  ASSISTANT_STALE_TURN_AFTER_MS,
  buildSubjectKey,
  type AssistantScope,
  type AssistantSubject,
  type AssistantThread,
  type AssistantThreadsQuery,
  type AssistantTurnStatus,
} from '@aica/api-types/schemas';

/** Polling cadence (§8.1): every 2 s; `org` slows to 10 s after two minutes. */
export const ASSISTANT_POLL_INTERVAL_MS = 2_000;
export const ASSISTANT_POLL_SLOW_INTERVAL_MS = 10_000;
export const ASSISTANT_POLL_SLOW_AFTER_MS = 120_000;
/**
 * `record`/`entity`: the edge's proxy answers first (§7.5, 503
 * `llm_unavailable` at `ASSISTANT_PROXY_DEADLINE_MS`); ten seconds past it
 * without a terminal status is the client's own `timeout` state.
 */
export const ASSISTANT_RECORD_TIMEOUT_MS = ASSISTANT_PROXY_DEADLINE_MS + 10_000;
/**
 * `org`: the server deadline is 10 minutes plus queue time, and the server
 * ends the turn itself with `stale_timeout` — the client only stops asking.
 */
export const ASSISTANT_ORG_DEADLINE_MS = 15 * 60_000;
/**
 * The server settles a `pending`/`running` turn as `failed`/`stale_timeout`
 * `ASSISTANT_STALE_TURN_AFTER_MS` after `createdAt` — in `GET /threads` too,
 * so history is re-read this long past that moment (clock skew between the
 * two) and again this often while the turn is still listed in flight.
 */
export const ASSISTANT_SETTLEMENT_GRACE_MS = 5_000;
export const ASSISTANT_SETTLEMENT_RETRY_MS = 30_000;

export function isAssistantTurnTerminal(
  status: AssistantTurnStatus | undefined
): boolean {
  return status === 'completed' || status === 'failed';
}

/** The flat query string `GET /threads` takes for a subject (§2.1). */
export function assistantThreadsQuery(subject: AssistantSubject): AssistantThreadsQuery {
  switch (subject.scope) {
    case 'record':
      return { scope: 'record', recordId: subject.recordId };
    case 'entity':
      return {
        scope: 'entity',
        entityType: subject.entityType,
        entityId: subject.entityId,
        ...(subject.installationId ? { installationId: subject.installationId } : {}),
      };
    case 'org':
      return { scope: 'org' };
  }
}

export function assistantThreadsQueryKey(
  selectedOrgId: string | null,
  subject: AssistantSubject
) {
  return [
    'assistant',
    'threads',
    selectedOrgId,
    subject.scope,
    buildSubjectKey(subject),
  ] as const;
}

/**
 * When history should be read again on its own: the earliest moment the
 * server settles a turn still in flight (`ASSISTANT_STALE_TURN_AFTER_MS` after
 * its `createdAt`, in the listing itself), or the retry step once that moment
 * has passed and the turn is still listed in flight — the server's clock may
 * be behind ours. `false` when nothing is in flight. A turn stuck on the
 * server is thus settled by one history read, never left "in progress" to
 * invite a second paid question (§3.7; Codex P1 round 5, P2 round 6).
 */
export function assistantHistoryRefetchMs(
  threads: readonly AssistantThread[] | undefined,
  now: number = Date.now()
): number | false {
  let next: number | false = false;
  for (const thread of threads ?? []) {
    for (const turn of thread.turns) {
      if (isAssistantTurnTerminal(turn.status)) continue;
      const createdAt = Date.parse(turn.createdAt);
      if (Number.isNaN(createdAt)) continue;
      const due =
        createdAt + ASSISTANT_STALE_TURN_AFTER_MS + ASSISTANT_SETTLEMENT_GRACE_MS;
      const wait = due > now ? due - now : ASSISTANT_SETTLEMENT_RETRY_MS;
      next = next === false ? wait : Math.min(next, wait);
    }
  }
  return next;
}

export function assistantThreadsOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  subject: AssistantSubject
) {
  return queryOptions({
    queryKey: assistantThreadsQueryKey(selectedOrgId, subject),
    enabled: !!selectedOrgId,
    // History is re-read on every open: an in-flight turn from a closed panel
    // must resume polling instead of provoking a second paid question (§3.7).
    staleTime: 0,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // The server settles a stuck turn in the listing: history is re-read at
    // that moment, in a background tab too, so a timed-out question is
    // never left «in progress» on screen.
    refetchInterval: (query) => assistantHistoryRefetchMs(query.state.data),
    refetchIntervalInBackground: true,
    retry: retryTransientOnly,
    queryFn: () => apiClient.assistant.listThreads(assistantThreadsQuery(subject)),
  });
}

export interface AssistantTurnPollParams {
  turnId: string;
  scope: AssistantScope;
  /** When the client started waiting — the ask, or the turn's `createdAt` on resume. */
  startedAt: number;
}

export function assistantTurnQueryKey(selectedOrgId: string | null, turnId: string) {
  return ['assistant', 'turn', selectedOrgId, turnId] as const;
}

/**
 * Next poll delay, or `false` to stop. Pure so the deadline table is testable
 * without a query client: terminal → stop; past the scope's deadline → stop;
 * `org` after two minutes → every 10 s; otherwise every 2 s.
 */
export function assistantTurnPollInterval(
  scope: AssistantScope,
  startedAt: number,
  status: AssistantTurnStatus | undefined,
  now: number = Date.now()
): number | false {
  if (isAssistantTurnTerminal(status)) return false;
  const elapsed = now - startedAt;
  if (scope === 'org') {
    if (elapsed > ASSISTANT_ORG_DEADLINE_MS) return false;
    return elapsed > ASSISTANT_POLL_SLOW_AFTER_MS
      ? ASSISTANT_POLL_SLOW_INTERVAL_MS
      : ASSISTANT_POLL_INTERVAL_MS;
  }
  return elapsed > ASSISTANT_RECORD_TIMEOUT_MS ? false : ASSISTANT_POLL_INTERVAL_MS;
}

export function assistantTurnOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null,
  params: AssistantTurnPollParams
) {
  return queryOptions({
    queryKey: assistantTurnQueryKey(selectedOrgId, params.turnId),
    enabled: !!selectedOrgId,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // The poll goes on in a background tab: the client deadline is wall-clock,
    // and a poll paused by the tab would declare a timeout the server had
    // already answered (owner's review, 17.09).
    refetchIntervalInBackground: true,
    retry: retryTransientOnly,
    refetchInterval: (query) =>
      assistantTurnPollInterval(params.scope, params.startedAt, query.state.data?.status),
    queryFn: () => apiClient.assistant.getTurn(params.turnId),
  });
}

export function assistantQuotaQueryKey(selectedOrgId: string | null) {
  return ['assistant', 'quota', selectedOrgId] as const;
}

export function assistantQuotaOptions(
  apiClient: ApiClient,
  selectedOrgId: string | null
) {
  return queryOptions({
    queryKey: assistantQuotaQueryKey(selectedOrgId),
    enabled: !!selectedOrgId,
    // Every answer carries a fresh `quota`; the hook writes it into this key,
    // so the peek is only needed when the panel opens.
    staleTime: 30 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: retryTransientOnly,
    queryFn: () => apiClient.assistant.getQuota(),
  });
}
